import React, { useCallback, forwardRef, useImperativeHandle, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, TextareaAutosize } from '@material-ui/core'
import Icon from '../../../../atoms/Icon'
import SydButton from '../../../../commonDesign/Button'
import FadeIn from '../../../../molecules/Transitions/FadeIn'

const useStyles = makeStyles((theme) => ({
  inputContainer: {
    padding: 'var(--dim-1) var(--dim-2)',
    backgroundColor: 'transparent'
  },
  inputContent: {
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto'
  },
  inputWrapper: {
    backgroundColor: 'var(--color-neutral-050)',
    border: '1px solid var(--color-neutral-600)',
    boxShadow: 'var(--shadow-sm)',
    borderRadius: '12px',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    transition: 'border-color 0.2s ease',
    '&:focus-within': {
      borderColor: 'var(--color-primary-500)'
    }
  },
  textarea: {
    width: '100%',
    backgroundColor: 'var(--color-neutral-050)',
    border: 'none',
    outline: 'none',
    resize: 'none',
    fontSize: '14px',
    lineHeight: 1.6,
    color: 'var(--font-color-main)',
    fontFamily: 'inherit',
    '&::placeholder': {
      color: 'var(--color-neutral-500)'
    }
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  tools: {
    display: 'flex',
    gap: '8px'
  },
  toolButton: {
    padding: '6px',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--color-neutral-400)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'var(--surface-bg-color-d3)',
      color: 'var(--color-primary-500)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },
  charCount: {
    fontSize: '12px',
    color: 'var(--color-neutral-500)'
  },
  submitSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  keyboardHint: {
    fontSize: '12px',
    color: 'var(--color-neutral-500)'
  }
}))

const MAX_LENGTH = 4000

const MessageInput = forwardRef(({ onSend, disabled, placeholder }, ref) => {
  const classes = useStyles()
  const textareaRef = useRef(null)
  // Fully self-contained state - no parent re-renders
  const [value, setValue] = React.useState('')
  const [charCount, setCharCount] = React.useState(0)
  const [hasContent, setHasContent] = React.useState(false)

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    setValue: (newValue) => {
      const trimmedValue = newValue.slice(0, MAX_LENGTH)
      setValue(trimmedValue)
      setCharCount(trimmedValue.length)
      setHasContent(!!trimmedValue.trim())
      // Focus the textarea
      if (textareaRef.current) {
        textareaRef.current.focus()
        // Move cursor to end
        textareaRef.current.setSelectionRange(trimmedValue.length, trimmedValue.length)
      }
    },
    focus: () => {
      textareaRef.current?.focus()
    },
    clear: () => {
      setValue('')
      setCharCount(0)
      setHasContent(false)
    }
  }), [])

  const handleKeyDown = useCallback((e) => {
    // Submit on Enter (without shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (value.trim() && !disabled) {
        onSend(value)
        // Clear the input after sending
        setValue('')
        setCharCount(0)
        setHasContent(false)
      }
    }
  }, [value, disabled, onSend])

  const handleSubmit = useCallback(() => {
    if (value.trim() && !disabled) {
      onSend(value)
      // Clear the input after sending
      setValue('')
      setCharCount(0)
      setHasContent(false)
    }
  }, [value, disabled, onSend])

  const handleFileAttach = useCallback(() => {
    // TODO: Implement file attachment
    console.log('Attach file')
  }, [])

  const handleChange = useCallback((e) => {
    const newValue = e.target.value.slice(0, MAX_LENGTH)
    setValue(newValue)
    setCharCount(newValue.length)
    setHasContent(!!newValue.trim())
  }, [])

  const remainingChars = MAX_LENGTH - charCount
  const isNearLimit = remainingChars < 100

  return (
    <FadeIn className={classes.inputContainer}>
      <div className={classes.inputContent}>
        <div className={classes.inputWrapper}>
          <TextareaAutosize
            ref={textareaRef}
            className={classes.textarea}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            minRows={1}
            maxRows={10}
          />

          <div className={classes.actions}>
            <div className={classes.tools}>
              <button
                className={classes.toolButton}
                onClick={handleFileAttach}
                disabled={disabled}
                title='Attach file'
              >
                <Icon name='attach_file' style={{ fontSize: '20px' }} />
              </button>
              <button
                className={classes.toolButton}
                disabled={disabled}
                title='Insert code block'
              >
                <Icon name='code' style={{ fontSize: '20px' }} />
              </button>
            </div>

            <div className={classes.submitSection}>
              <span
                className={classes.charCount} style={{
                  color: isNearLimit ? 'var(--color-warning-500)' : undefined
                }}
              >
                {remainingChars}
              </span>
              <span className={classes.keyboardHint}>
                {window.navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter
              </span>
              <SydButton
                size='sm'
                variant='primary'
                onClick={handleSubmit}
                disabled={disabled || !hasContent}
                processing={disabled}
              >
                <Icon name='navigateNext' style={{ fontSize: '18px', fill: 'var(--color-contrast-primary-500)' }} />
              </SydButton>
            </div>
          </div>
        </div>
      </div>
    </FadeIn>
  )
})

MessageInput.displayName = 'MessageInput'

MessageInput.propTypes = {
  onSend: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string
}

MessageInput.defaultProps = {
  disabled: false,
  placeholder: 'Type a message...'
}

export default MessageInput
